<template>
  <b-sidebar
    id="sidebar-yield-controlling"
    backdrop-variant="dark"
    backdrop
    shadow
    right
    bg-variant="white"
    header-class="p-0"
    width="max(58%, 840px)"
    @shown="onShow"
    @hidden="onHide"
  >
    <template #header="{ hide }">
      <SidebarHeader
        :isLoading="isLoading"
        :titleHref="activeReisetermin ? '#/reisetermine/' + activeReisetermin.id : null"
        :kuerzel="activeReisetermin?.reiseterminkuerzel?.substring(0, 6)"
        :datum="startdatumFormatted"
        :ursprung="
          activeReisetermin?.ursprungsreiseterminId
            ? {
                id: activeReisetermin.ursprungsreiseterminId,
                kuerzel: activeReisetermin.ursprungsreisetermin?.reiseterminkuerzel,
              }
            : null
        "
        :ersatz="
          activeReisetermin?.ersatztermin
            ? {
                id: activeReisetermin.ersatztermin.id,
                kuerzel: activeReisetermin.ersatztermin.reiseterminkuerzel,
              }
            : null
        "
        @go-up="goUpOneRow"
        @go-down="goDownOneRow"
        @close="hide"
      >
        <template #actions>
          <template v-if="activeReisetermin">
            <b-button
              class="btn btn-icon icon-button"
              size="sm"
              v-b-tooltip.hover.bottom
              title="Neu laden"
              :disabled="isLoading || !disableSpeichernButton"
              @click="onReload"
            >
              <RotateCcw :size="16" />
            </b-button>
            <b-button
              class="btn btn-icon icon-button"
              size="sm"
              v-b-tooltip.hover.bottom
              title="Reisestammdaten"
              :href="
                '#/reisen/' +
                activeReisetermin.reiseterminkuerzel?.substring(0, 6) +
                '/reisestammdaten?reiseterminAbreisedatum=' +
                activeReisetermin.abreisedatum.substring(0, 10)
              "
              target="_blank"
            >
              <Pencil :size="16" />
            </b-button>
            <b-button
              class="btn btn-icon icon-button"
              size="sm"
              v-b-tooltip.hover.bottom
              title="Kontaktdatenliste"
              :href="kontaktdatenlisteLink"
              target="_blank"
            >
              <Contact :size="16" />
            </b-button>
            <b-button
              class="btn btn-icon icon-button"
              size="sm"
              v-b-tooltip.hover.bottom
              title="Aufgabe erstellen"
              v-b-modal.modalAufgabeEdit
              @click="openModalWithData"
            >
              <ListChecks :size="16" />
            </b-button>
            <b-button
              class="dialog-btn"
              size="sm"
              @click="saveChanges"
              :variant="disableSpeichernButton ? 'secondary' : 'primary'"
              :disabled="disableSpeichernButton"
            >
              Speichern
            </b-button>
          </template>
        </template>
      </SidebarHeader>
    </template>
    <b-overlay :show="isLoading" opacity="0.95" style="min-height: 250px">
      <div class="sidebar-content" v-if="reiseterminExists && Object.keys(this.activeReisetermin).length > 0">
        <SidebarFieldSection header="Reisetermindaten" v-model="reisetermindaten" />
        <SidebarFieldSection class="mt-10" header="Flugdaten" v-model="flugdaten" />

        <SidebarFlugplanungInlandsfluege :dataInlandsfluege="dataInlandsfluege" />

        <SidebarFieldSection class="mt-10" header="Einkaufsdaten" v-model="einkaufsdaten" />

        <div v-if="aufgabenYieldControlling?.length > 0" class="mt-12">
          <SidebarSection title="Aufgaben" :card="false" collapsedOnMount>
            <AufgabenWidget
              :filter="'sidebar-yield-controlling'"
              :ansicht="'sidebar-yield-controlling'"
              :aufgaben="aufgabenYieldControlling || []"
            ></AufgabenWidget>
          </SidebarSection>
        </div>
      </div>
    </b-overlay>
  </b-sidebar>
</template>

<script>
import { format, add, getISODay } from 'date-fns';

import PaxWidget from '@/components/produkte/reisetermine/pax-widget.vue';
import CollapsibleWithItems from '@/components/common/collapsible-with-items.vue';
import AufgabenWidget from '@/components/produkte/reisetermine/aufgaben-widget.vue';
import buildQuery from 'odata-query';
import apiService from '@/core/common/services/api.service';
import { translateEinkaufsartOptions, translateStatusGruppeOptions } from '@/core/common/helpers/utils.js';
import { Contact, ListChecks, Pencil, RotateCcw } from 'lucide-vue';
import SidebarHeader from '@/components/common/sidebar-section/sidebar-header.vue';
import SidebarSection from '@/components/common/sidebar-section/sidebar-section.vue';
import SidebarFieldSection from '@/components/common/sidebar-section/sidebar-field-section.vue';
import SidebarFlugplanungInlandsfluege from '@/components/produkte/reisetermine/sidebar-flugplanung/sidebar-flugplanung-inlandsfluege.vue';

export default {
  components: {
    PaxWidget,
    CollapsibleWithItems,
    AufgabenWidget,
    Contact,
    ListChecks,
    Pencil,
    RotateCcw,
    SidebarHeader,
    SidebarSection,
    SidebarFieldSection,
    SidebarFlugplanungInlandsfluege,
  },
  props: { reiseterminId: { type: Number } },
  data() {
    return {
      prefixedVerlinkungen: [],
      activeReisetermin: null,
      isLoading: false,
      reisetermindaten: [],
      flugdaten: [],
      einkaufsdaten: [],
      resultsAirlines: [],
    };
  },
  watch: {
    reiseterminId: {
      immediate: true,
      async handler(id) {
        if (id != null) {
          await this.reloadAktivenTermin();
        }
      },
    },
  },
  computed: {
    reiseterminExists() {
      return this.activeReisetermin && Object.keys(this.activeReisetermin).length > 0;
    },
    disableSpeichernButton() {
      return this.noFlugdatenChanges;
    },
    noFlugdatenChanges() {
      return this.flugdaten
        .filter(item => (!Array.isArray(item.value) && item.editable) || item.editable?.[0])
        .every(
          item =>
            item.value === item.valueEdited ||
            JSON.stringify(item.value)?.replaceAll('[', '').replaceAll(']', '') ===
              JSON.stringify(item.valueEdited)?.replaceAll('[', '').replaceAll(']', '')
        );
    },
    kontaktdatenlisteLink() {
      if (!this.activeReisetermin) {
        return null;
      }
      const startdatumYear = new Date(this.activeReisetermin.startdatum).getFullYear();
      return startdatumYear === 2022
        ? 'https://docs.google.com/spreadsheets/d/1jLZQswDFvOvXLT9qlg44QPP5tDp0mcvt5z0PYof_GG8/edit?usp=sharing'
        : startdatumYear === 2023
        ? 'https://docs.google.com/spreadsheets/d/1ISyLNU-nPPi_M7Yrtm_r55Q789Y_9xdpUlRXZpbW-5k/edit#gid=0'
        : startdatumYear === 2024
        ? 'https://docs.google.com/spreadsheets/d/1mf6SBNPv8NNBUulHYNA1XGyKcPxrp3nsRz7Nyx6Har0/edit#gid=0'
        : startdatumYear === 2025 || startdatumYear === 2026
        ? 'https://docs.google.com/spreadsheets/d/1OkrSdCu1SskISUsioOWzJhi1M4lCqhAtScsvcdFMrWo/edit?gid=0#gid=0'
        : null;
    },
    aufgabenYieldControlling() {
      if (!this.activeReisetermin || this.activeReisetermin.aufgaben?.length === 0) {
        return null;
      }

      return this.activeReisetermin.aufgaben;
    },
    dataInlandsfluege() {
      if (this.reiseterminExists) {
        return (this.activeReisetermin.metadata?.inlandsfluege ?? [])
          .slice()
          .sort((a, b) => (a.tag === b.tag ? 0 : a.tag > b.tag ? 1 : -1))
          .map(inlandsflug => {
            return [
              {
                label: 'Tag / Verkehrstag / Datum',
                value:
                  inlandsflug.tag +
                  ' / ' +
                  this.getVerkehrstagOfInlandsflug(this.activeReisetermin, inlandsflug) +
                  ' / ' +
                  this.getDateOfIlandsflug(this.activeReisetermin, inlandsflug),
                renderer: null,
                variant: null,
                show: true,
                // Die Einzelwerte stehen in der Kopfzeile der Inlandsflug-Karte
                meta: [
                  { label: 'Tag', wert: inlandsflug.tag },
                  {
                    label: 'Verkehrstag',
                    wert: this.getVerkehrstagOfInlandsflug(this.activeReisetermin, inlandsflug),
                  },
                  { label: 'Datum', wert: this.getDateOfIlandsflug(this.activeReisetermin, inlandsflug) },
                ],
              },
              {
                label: 'Abflughafen',
                value: inlandsflug.abflughafenIataCode,
                renderer: null,
                variant: null,
                show: true,
              },
              {
                label: 'Zielflughafen',
                value: inlandsflug.zielflughafenIataCode,
                renderer: null,
                variant: null,
                show: true,
              },
              {
                label: 'Früh. Abflug',
                value: inlandsflug.fruehesterAbflug?.substring(0, 5) ?? '-',
                renderer: null,
                variant: null,
                show: inlandsflug.minimaleUmsteigezeit == null,
              },
              {
                label: 'Spät. Ankunft',
                value: inlandsflug.spaetesteAnkunft?.substring(0, 5) ?? '-',
                renderer: null,
                variant: null,
                show: inlandsflug.minimaleUmsteigezeit == null,
              },
              {
                label: 'Id',
                value: inlandsflug.id,
                show: false,
              },
            ];
          });
      } else return [];
    },
    startdatumFormatted() {
      if (!this.activeReisetermin?.startdatum) {
        return '';
      }
      return format(new Date(this.activeReisetermin.startdatum), 'dd.MM.yyyy');
    },
  },
  methods: {
    // Lädt den aktiven Reisetermin neu und baut die Datenblöcke neu auf
    async reloadAktivenTermin() {
      await this.loadReisetermin(this.reiseterminId);
      this.setReisetermindaten();
      this.setFlugdaten();
      this.setEinkaufsdaten();
    },
    onReload() {
      this.reloadAktivenTermin();
      this.$emit('reload');
    },
    async loadReisetermin(id) {
      const odataQuery = buildQuery({
        filter: [{ id: { eq: id } }],
        expand: {
          reise: {},
          flugeinkauf: { expand: { Gruppeneinkaeufe: {} } },
          gueltigeSaison: {
            expand: { ziellandZielflughaefen: {}, ziellandAbflughaefen: {}, Inlandsfluege: {} },
          },
          gueltigerSaisonpreis: { select: ['thMaxLangstrecke', 'thMaxInlandsflug'] },
          metadata: {
            expand: {
              ziellandZielflughaefen: {},
              ziellandAbflughaefen: {},
              Inlandsfluege: {},
            },
            select: [
              'minPax',
              'maxPax',
              'kalkPax',
              'forecastPax',
              'durchfuehrbareMinPax',
              'planPax',
              'hotelnaechte',
              'transferszenarioHin',
              'transferszenarioRueck',
              'fruehesteAnkunft',
              'spaetesteAnkunft',
              'fruehesterAbflug',
              'spaetesterAbflug',
              'restriktionenHinRollover',
              'restriktionenRueckRollover',
              'overnightOutbound',
              'overnightInbound',
            ],
          },
          aufgaben: {},
          ursprungsreisetermin: {},
          ersatztermin: {},
          pax: {},
        },
      });
      this.isLoading = true;
      const response = await apiService.get('/Reisetermin', odataQuery);
      this.activeReisetermin = response.data.result.value[0];
      this.isLoading = false;
    },
    setReisetermindaten() {
      this.reisetermindaten = [
        {
          label: 'Priorität',
          value: this.activeReisetermin.reise.prioritaet,
          renderer: 'prioritaetWidget',
          variant: null,
        },
        {
          label: 'Plan Pax',
          column: 2,
          value: this.activeReisetermin.metadata?.planPax ?? '-',
          variant: null,
          renderer: null,
        },
        {
          label: 'Hotelnächte',
          value: this.activeReisetermin.metadata?.hotelnaechte ?? '-',
          variant: null,
          renderer: null,
        },
        {
          label: 'Pax',
          value: this.activeReisetermin,
          renderer: 'paxWidget',
          variant: null,
          className: 'mb-2',
        },
        {
          label: 'Transferszenario Hin/Rück',
          column: 2,
          value: null,
          renderer:
            (this.activeReisetermin.metadata.transferszenarioHin?.substring(0, 2) ?? '-') +
            ' / ' +
            (this.activeReisetermin.metadata.transferszenarioRueck?.substring(0, 2) ?? '-'),
          variant: null,
        },
      ];
    },
    setFlugdaten() {
      this.flugdaten = [
        {
          label: 'Strecke',
          value:
            (this.activeReisetermin.metadata.ziellandZielflughaefen
              ?.map(fh => fh.flughafenIataCode)
              .join('/') ?? '') +
            '-' +
            (this.activeReisetermin.metadata.ziellandAbflughaefen
              ?.map(fh => fh.flughafenIataCode)
              .join('/') ?? ''),
          renderer: null,
          variant: null,
        },
        {
          label: ['OB', 'IB'],
          value: [
            this.activeReisetermin?.metadata.overnightOutbound ?? '-',
            this.activeReisetermin?.metadata.overnightInbound ?? '-',
          ],
          renderer: null,
          editable: [false, false],
          split: true,
          paramLabel: ['overnightOutbound', 'overnightInbound'],
        },
        {
          label: 'Abreise',
          value: this.activeReisetermin.abreisedatum
            ? format(new Date(this.activeReisetermin.abreisedatum), 'dd.MM.yyyy')
            : '-',
          renderer: null,
          variant: null,
        },
        {
          label: 'Rückflugdatum',
          value: this.activeReisetermin.hotelendUndRueckflugdatum
            ? format(new Date(this.activeReisetermin.hotelendUndRueckflugdatum), 'dd.MM.yyyy')
            : '-',
          renderer: null,
          variant: null,
        },
        {
          label: ['Verkehrstag Hin', 'Rück'],
          value: [this.activeReisetermin?.verkehrstagHin, this.activeReisetermin?.verkehrstagRueck],
          renderer: null,
          editable: [false, false],
          split: true,
          paramLabel: ['verkehrstagHin', 'verkehrstagRück'],
        },
        {
          label: ['Hin: Früheste', 'Späteste Ankunft'],
          column: 2,
          value: [
            `${this.activeReisetermin?.metadata?.fruehesteAnkunft?.substring(0, 5)} Uhr`,
            `${this.activeReisetermin?.metadata?.spaetesteAnkunft?.substring(0, 5)} Uhr ${
              this.activeReisetermin?.metadata.restriktionenHinRollover ? '+1' : ''
            }`,
          ],

          renderer: null,
          editable: [false, false],
          editType: ['time', 'time'],
          split: true,
          paramLabel: ['fruehesteAnkunft', 'spaetesteAnkunft'],
        },
        {
          label: ['Rück: Frühester', 'Spätester Abflug'],
          column: 2,
          value: [
            `${this.activeReisetermin?.metadata?.fruehesterAbflug?.substring(0, 5)} Uhr`,
            `${this.activeReisetermin?.metadata?.spaetesterAbflug?.substring(0, 5)} Uhr ${
              this.activeReisetermin?.metadata.restriktionenRueckRollover ? '+1' : ''
            }`,
          ],
          renderer: null,
          editable: [false, false],
          editType: ['time', 'time'],
          split: true,
          paramLabel: ['fruehesterAbflug', 'spaetesterAbflug'],
        },
        {
          label: ['TH Langstrecke', 'TH Inlandsflug'],
          column: 2,
          value: [this.activeReisetermin?.thLangstrecke ?? '-', this.activeReisetermin?.thInlandsflug ?? '-'],
          valueEdited: [
            this.activeReisetermin?.thLangstrecke ?? '-',
            this.activeReisetermin?.thInlandsflug ?? '-',
          ],
          renderer: null,
          editable: [true, true],
          editType: ['number', 'number'],
          split: true,
          paramLabel: ['thLangstrecke', 'thInlandsflug'],
          append: ['€', '€'],
        },
        {
          label: ['TH Max Langstrecke', 'TH Max Inlandsflug'],
          column: 2,
          value: [
            this.activeReisetermin?.gueltigerSaisonpreis?.thMaxLangstrecke ?? '-',
            this.activeReisetermin?.gueltigerSaisonpreis?.thMaxInlandsflug ?? '-',
          ],
          renderer: null,
          editable: [false, false],
          editType: ['number', 'number'],
          split: true,
          append: ['€', '€'],
        },
      ];
    },
    setEinkaufsdaten() {
      this.einkaufsdaten = [
        {
          label: 'Status Gruppe',
          value: this.activeReisetermin.flugeinkauf?.statusgruppe ?? '-',
          renderer: translateStatusGruppeOptions(this.activeReisetermin.flugeinkauf?.statusgruppe),
          editable: false,
        },
        {
          label: 'Einkaufsart',
          column: 2,
          value: this.activeReisetermin.flugeinkauf?.einkaufsart,
          renderer: translateEinkaufsartOptions(this.activeReisetermin.flugeinkauf?.einkaufsart),
          editable: false,
        },
        {
          label: 'GRP FH (Gesamtpreis GRP)',
          value:
            this.activeReisetermin.flugeinkauf?.gruppeneinkaeufe?.length > 0
              ? this.activeReisetermin.flugeinkauf.gruppeneinkaeufe
                  .map(
                    grp =>
                      `${grp.flughafenIataCode || ''}(${
                        grp.gesamtpreisGruppeneinkauf ? Math.round(grp.gesamtpreisGruppeneinkauf) + '€' : '0€'
                      })`
                  )
                  .filter(Boolean)
                  .join(', ')
              : '-',
          renderer: null,
          editable: false,
        },
      ];
    },

    goUpOneRow() {
      this.$emit('rowSelectionChanged', 'up');
    },
    goDownOneRow() {
      this.$emit('rowSelectionChanged', 'down');
    },
    async saveChanges() {
      this.isLoading = true;

      const thWerteParams = this.formatTHWerte();
      const reisekuerzel = this.activeReisetermin.reiseterminkuerzel.substring(0, 6);

      // Prüfe TH-Werte Änderungen
      const objectThWerte = this.flugdaten.find(element => element.label[0] === 'TH Langstrecke');
      const isChangedThWerte =
        objectThWerte.value[0] != objectThWerte.valueEdited[0] ||
        objectThWerte.value[1] != objectThWerte.valueEdited[1];

      try {
        // API-Aufruf für TH-Werte wenn verändert wurden
        if (isChangedThWerte) {
          await apiService
            .put(`Reise/${reisekuerzel}/reisetermine/bulkUpdateReiseterminValues`, thWerteParams)
            .then(response => {
              this.$emit('thWerteBearbeitet', response);
              this.toast('TH-Werte erfolgreich gespeichert.');
            });
        }

        await this.loadReisetermin(this.reiseterminId);
        this.setReisetermindaten();
        this.setFlugdaten();
        this.setEinkaufsdaten();
      } catch (error) {
        console.error('Fehler beim Speichern:', error);
        this.toast('Fehler beim Speichern der Daten.', 'error');
        this.isLoading = false;
      } finally {
        this.isLoading = false;
      }
    },
    formatTHWerte() {
      // Hole das TH-Werte Objekt aus flugdaten
      const objectThWerte = this.flugdaten.find(element => element.label[0] === 'TH Langstrecke');

      const thWerteParams = {
        [this.activeReisetermin.startdatum]: {
          thLangstrecke: objectThWerte.valueEdited[0] === '-' ? null : +objectThWerte.valueEdited[0] || null,
          thInlandsflug: objectThWerte.valueEdited[1] === '-' ? null : +objectThWerte.valueEdited[1] || null,
        },
      };

      console.log('formatTHWerte result:', thWerteParams);
      return thWerteParams;
    },
    openModalWithData() {
      this.prefixedVerlinkungen = [
        {
          id: 0,
          typ: 'Reisetermin',
          artifactId: this.activeReisetermin.id,
          artifactText: this.activeReisetermin.reiseterminkuerzel,
        },
      ];
      this.$emit('showModalAufgabeEdit', this.prefixedVerlinkungen);
    },
    onShow() {
      this.addKeyEventListener();
    },
    onHide() {
      this.resultsAirlines = [];
      this.$emit('sidebarHide');
      this.removeKeyEventListener();
    },
    addKeyEventListener() {
      // Placeholder implementation
    },
    removeKeyEventListener() {
      // Placeholder implementation
    },
    getVerkehrstagOfInlandsflug(reisetermin, inlandsflug) {
      const dayOfInlandsflug = add(new Date(reisetermin.abreisedatum), { days: +inlandsflug.tag - 1 });
      const verkehrstag = getISODay(dayOfInlandsflug);
      return verkehrstag;
    },
    getDateOfIlandsflug(reisetermin, inlandsflug) {
      const dateOfInlandsfluge = add(new Date(reisetermin.abreisedatum), { days: +inlandsflug.tag - 1 });
      return format(dateOfInlandsfluge, 'dd.MM.yyyy');
    },
  },
};
</script>

<style scoped>
:deep(.b-sidebar) {
  border-radius: var(--radius-lg) 0 0 var(--radius-lg);
}
.sidebar-content {
  width: 100%;
  padding: 24px;
}
</style>
